// crewbit-admin/src/pages/Vendors/VendorFormPanel.tsx
//
// Wave 7 Step 4 — admin vendor create + edit + archive panel. Mirrors the
// Clients/ClientFormPanel pattern (SlidePanel + sticky footer). One panel,
// two modes via the `vendor` prop:
//   - vendor = null → create (calls createVendor; no archive button)
//   - vendor = Vendor → edit (calls updateVendor; shows archive button)
//
// Partial-update via the update_vendor RPC: only fields that differ from
// the loaded vendor are sent (omitted args preserve existing column;
// empty-string clears a nullable text col). company_name is required;
// status comes through the archive button only ('active' is the default).

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Archive } from 'lucide-react';
import { palette, spacing, radii, transition, admin } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { Toast } from '../../components/common/Toast';
import { useAuthStore } from '../../stores/authStore';
import { createVendor, updateVendor, archiveVendor } from '../../lib/vendorBillMutations';
import { lookupErrorCopy } from '../../lib/errorMap';
import type { Vendor } from '../../types';

type PaymentTerms = NonNullable<Vendor['payment_terms']>;

const TERMS: { value: PaymentTerms; label: string }[] = [
  { value: 'on_receipt', label: 'On receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_60', label: 'Net 60' },
];

interface Props {
  isOpen: boolean;
  vendor: Vendor | null;
  onClose: () => void;
  onSaved: (newVendorId?: string) => void;
  onArchived: () => void;
}

interface FormState {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  serviceType: string;
  paymentTerms: PaymentTerms;
  businessNumber: string;
  defaultTaxType: string;
  notes: string;
}

const EMPTY: FormState = {
  companyName: '', contactName: '', email: '', phone: '', address: '',
  serviceType: '', paymentTerms: 'net_30', businessNumber: '', defaultTaxType: '', notes: '',
};

function fromVendor(v: Vendor): FormState {
  return {
    companyName: v.company_name,
    contactName: v.contact_name ?? '',
    email: v.email ?? '',
    phone: v.phone ?? '',
    address: v.address ?? '',
    serviceType: v.service_type ?? '',
    paymentTerms: v.payment_terms ?? 'net_30',
    businessNumber: v.business_number ?? '',
    defaultTaxType: v.default_tax_type ?? '',
    notes: v.notes ?? '',
  };
}

export function VendorFormPanel({ isOpen, vendor, onClose, onSaved, onArchived }: Props) {
  const businessId = useAuthStore((s) => s.business?.id);
  const [state, setState] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Hydrate when the panel opens or the vendor swaps. The set-state-in-effect
  // pattern matches MoneyExpenseEditPanel's hydration (the "page's filtered
  // slice may not include this row" rationale).
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!isOpen) return;
    setState(vendor ? fromVendor(vendor) : EMPTY);
  }, [isOpen, vendor]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const update = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((s) => ({ ...s, [key]: value }));
  }, []);

  const isEdit = vendor !== null;
  const valid = state.companyName.trim().length > 0;

  const dirty = useMemo(() => {
    if (!isEdit || !vendor) return Object.values(state).some((v) => typeof v === 'string' && v.length > 0);
    const fresh = fromVendor(vendor);
    return (Object.keys(state) as Array<keyof FormState>).some((k) => state[k] !== fresh[k]);
  }, [state, vendor, isEdit]);

  const handleSave = useCallback(async () => {
    if (!valid) return;
    setLoading(true);
    if (isEdit && vendor) {
      const res = await updateVendor({
        vendorId: vendor.id,
        companyName: state.companyName.trim(),
        contactName: state.contactName.trim(),
        email: state.email.trim(),
        phone: state.phone.trim(),
        address: state.address.trim(),
        serviceType: state.serviceType.trim(),
        paymentTerms: state.paymentTerms,
        businessNumber: state.businessNumber.trim(),
        defaultTaxType: state.defaultTaxType.trim(),
        notes: state.notes.trim(),
      });
      setLoading(false);
      if (!res.success) {
        setToast({ msg: lookupErrorCopy('update_vendor', res.error.code), type: 'error' });
        return;
      }
      setToast({ msg: 'Vendor updated.', type: 'success' });
      onSaved();
      onClose();
    } else {
      if (!businessId) return;
      const res = await createVendor({
        businessId,
        companyName: state.companyName.trim(),
        contactName: state.contactName.trim() || null,
        email: state.email.trim() || null,
        phone: state.phone.trim() || null,
        address: state.address.trim() || null,
        serviceType: state.serviceType.trim() || null,
        paymentTerms: state.paymentTerms,
        businessNumber: state.businessNumber.trim() || null,
      });
      setLoading(false);
      if (!res.success) {
        setToast({ msg: lookupErrorCopy('create_vendor', res.error.code), type: 'error' });
        return;
      }
      setToast({ msg: res.data.idempotent_replay ? 'Vendor already exists.' : 'Vendor added.', type: 'success' });
      onSaved(res.data.vendor_id);
      onClose();
    }
  }, [valid, isEdit, vendor, state, businessId, onSaved, onClose]);

  const handleArchive = useCallback(async () => {
    if (!vendor) return;
    setArchiving(true);
    const res = await archiveVendor(vendor.id);
    setArchiving(false);
    setConfirmArchive(false);
    if (!res.success) {
      setToast({ msg: lookupErrorCopy('update_vendor', res.error.code), type: 'error' });
      return;
    }
    setToast({ msg: 'Vendor archived.', type: 'success' });
    onArchived();
    onClose();
  }, [vendor, onArchived, onClose]);

  return (
    <>
      <SlidePanel isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit vendor' : 'Add vendor'} width={560}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <Row>
            <Field label="Company name *" flex={1}>
              <input value={state.companyName} onChange={(e) => update('companyName', e.target.value.slice(0, 200))} style={INPUT} autoFocus />
            </Field>
          </Row>
          <Row>
            <Field label="Contact name" flex={1}>
              <input value={state.contactName} onChange={(e) => update('contactName', e.target.value.slice(0, 200))} style={INPUT} />
            </Field>
            <Field label="Phone" flex={1}>
              <input value={state.phone} onChange={(e) => update('phone', e.target.value.slice(0, 50))} style={INPUT} />
            </Field>
          </Row>
          <Row>
            <Field label="Email" flex={1}>
              <input value={state.email} onChange={(e) => update('email', e.target.value.slice(0, 200))} style={INPUT} />
            </Field>
            <Field label="Service type" flex={1}>
              <input value={state.serviceType} onChange={(e) => update('serviceType', e.target.value.slice(0, 100))} style={INPUT} placeholder="e.g. Materials" />
            </Field>
          </Row>
          <Row>
            <Field label="Address" flex={1}>
              <input value={state.address} onChange={(e) => update('address', e.target.value.slice(0, 500))} style={INPUT} />
            </Field>
          </Row>
          <Row>
            <Field label="Payment terms" flex={1}>
              <select value={state.paymentTerms} onChange={(e) => update('paymentTerms', e.target.value as PaymentTerms)} style={INPUT}>
                {TERMS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Default tax type" flex={1}>
              <input value={state.defaultTaxType} onChange={(e) => update('defaultTaxType', e.target.value.slice(0, 50))} style={INPUT} placeholder="e.g. HST" />
            </Field>
          </Row>
          <Row>
            <Field label="Business number" flex={1}>
              <input value={state.businessNumber} onChange={(e) => update('businessNumber', e.target.value.slice(0, 100))} style={INPUT} />
            </Field>
          </Row>
          <Field label="Notes">
            <textarea value={state.notes} onChange={(e) => update('notes', e.target.value)} rows={3} style={{ ...INPUT, resize: 'vertical' }} />
          </Field>
        </div>

        <div style={FOOTER}>
          {isEdit ? (
            <button type="button" onClick={() => setConfirmArchive(true)} style={ARCHIVE_BTN}>
              <Archive size={13} />
              Archive
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <button type="button" onClick={onClose} style={SECONDARY_BTN}>Cancel</button>
            <button type="button" disabled={!valid || loading || (isEdit && !dirty)} onClick={() => void handleSave()}
              style={{ ...PRIMARY_BTN, background: (!valid || loading || (isEdit && !dirty)) ? palette.g300 : palette.accent }}>
              {loading ? 'Saving…' : (isEdit ? 'Save changes' : 'Add vendor')}
            </button>
          </div>
        </div>
      </SlidePanel>

      <ConfirmDialog
        isOpen={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() => void handleArchive()}
        title="Archive this vendor?"
        message="Archived vendors are hidden from your list and pickers but stay linked to past expenses and bills."
        confirmLabel={archiving ? 'Archiving…' : 'Archive'}
        confirmVariant="danger"
        loading={archiving}
      />

      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </>
  );
}

// ── Inline UI helpers (kept local to avoid a separate .styles.ts for a single panel) ──

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: spacing.md }}>{children}</div>;
}
function Field({ label, flex, children }: { label: string; flex?: number; children: React.ReactNode }) {
  return (
    <div style={{ flex }}>
      <label style={LABEL}>{label}</label>
      {children}
    </div>
  );
}

const LABEL: CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 6 };
const INPUT: CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: radii.sm, border: `1px solid ${palette.g200}`, background: palette.white, fontFamily: 'inherit', fontSize: 13.5, color: palette.g800, outline: 'none', boxSizing: 'border-box' };
const FOOTER: CSSProperties = { position: 'sticky', bottom: -20, marginLeft: -20, marginRight: -20, marginTop: spacing.lg, padding: `${spacing.md}px ${spacing.lg}px`, background: palette.white, borderTop: `1px solid ${admin.border}`, display: 'flex', gap: spacing.sm, justifyContent: 'space-between', alignItems: 'center' };
const SECONDARY_BTN: CSSProperties = { padding: '9px 18px', borderRadius: radii.sm, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13, fontWeight: 600, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' };
const PRIMARY_BTN: CSSProperties = { padding: '9px 22px', borderRadius: radii.sm, border: 'none', color: palette.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
const ARCHIVE_BTN: CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: radii.sm, border: `1px solid ${palette.g200}`, background: palette.white, color: palette.red, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: `background ${transition.fast}` };
